import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';

import CreateInternalOrderDialog from '../components/CreateInternalOrderDialog';
import DeleteInternalOrderDialog from '../components/DeleteInternalOrderDialog';
import PageHeader from '../components/PageHeader';
import SortableHeader from '../components/SortableHeader';
import { useTableSort } from '../utils/tableSort';
import BusinessUnitChip from '../components/BusinessUnitChip';
import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import { InternalOrder, InternalOrderStatus } from '../types';
import { apiErrorMessage } from '../utils/apiError';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function ProductionListPage() {
  const navigate = useNavigate();
  const { isKalotteUser } = useAuth();
  const { sort, order, onSort, sortRows } = useTableSort<
    'auftragsnummer' | 'business_unit' | 'coil_count' | 'grade_count' | 'created_at'
  >('created_at', 'desc', ['created_at']);
  const [tab, setTab] = useState<InternalOrderStatus>('open');
  const [orders, setOrders] = useState<InternalOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<InternalOrder | null>(null);
  const [search, setSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.listInternalOrders({
        status: tab,
        q: debouncedSearch.trim() || undefined,
      });
      setOrders(data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Aufträge konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleOrders = sortRows(orders, {
    auftragsnummer: (item) => item.auftragsnummer,
    business_unit: (item) => item.business_unit,
    coil_count: (item) => item.coil_count,
    grade_count: (item) => item.grade_count,
    created_at: (item) => item.created_at,
  });

  return (
    <Box>
      <PageHeader
        title="Produktion"
        actions={
          <>
            {isKalotteUser && (
              <TextField
                size="small"
                label="Suche"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Auftragsnummer suchen…"
                sx={{ minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Neuer Auftrag
            </Button>
          </>
        }
      />

      <Tabs value={tab} onChange={(_e, v) => setTab(v as InternalOrderStatus)} sx={{ mb: 2 }}>
        <Tab value="open" label="Offene Aufträge" />
        <Tab value="closed" label="Abgeschlossen" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {tab === 'open' ? 'Keine offenen Aufträge.' : 'Keine abgeschlossenen Aufträge.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeader column="auftragsnummer" label="Auftragsnummer" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="business_unit" label="Bereich" sort={sort} order={order} onSort={onSort} />
                <SortableHeader column="coil_count" label="Coils" sort={sort} order={order} onSort={onSort} align="right" />
                <SortableHeader column="grade_count" label="Werkstoffe" sort={sort} order={order} onSort={onSort} align="right" />
                <SortableHeader column="created_at" label="Erstellt" sort={sort} order={order} onSort={onSort} />
                <TableCell align="right">Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleOrders.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {o.auftragsnummer}
                  </TableCell>
                  <TableCell>
                    <BusinessUnitChip unit={o.business_unit} />
                  </TableCell>
                  <TableCell align="right">{o.coil_count}</TableCell>
                  <TableCell align="right">{o.grade_count}</TableCell>
                  <TableCell>{formatDate(o.created_at)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => navigate(`/production/${o.id}`)}>
                      Öffnen
                    </Button>
                    {isKalotteUser && (
                      <Tooltip title="Auftrag löschen">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Auftrag ${o.auftragsnummer} löschen`}
                          onClick={() => setOrderToDelete(o)}
                        >
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateInternalOrderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(order) => {
          setDialogOpen(false);
          navigate(`/production/${order.id}`);
        }}
      />

      <DeleteInternalOrderDialog
        order={orderToDelete}
        onClose={() => setOrderToDelete(null)}
        onDeleted={() => {
          setOrderToDelete(null);
          void load();
        }}
      />
    </Box>
  );
}
